import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ApplicationSidebar } from '../../components/applications/ApplicationSidebar';
import { DocumentsSection } from '../../components/applications/DocumentsSection';
import { DownPaymentSection } from '../../components/applications/DownPaymentSection';
import { IncomeSection } from '../../components/applications/IncomeSection';
import { RateAITab } from '../../components/applications/RateAITab';
import { CheckCircleIcon, XCircleIcon, AlertTriangleIcon, FileTextIcon, ClipboardCheckIcon, UserCheckIcon, CreditCardIcon, BrainIcon, FileIcon, CheckIcon, ExternalLinkIcon, DownloadIcon, PlusIcon, ChevronDownIcon, ChevronRightIcon } from 'lucide-react';
// Mock application data
const mockApplication = {
  id: 'app-1234',
  borrower: 'John Smith',
  applicationId: 'APP-2023-001',
  status: 'In Progress',
  loanType: 'Conventional',
  loanAmount: 350000,
  propertyAddress: '123 Main Street, Toronto, ON',
  createdAt: '2023-05-15',
  updatedAt: '2023-05-20',
  kycStatus: 'Verified',
  downPaymentVerification: {
    threshold: 10000,
    period: {
      start: '2023-02-15',
      end: '2023-05-15',
      days: 90
    }
  },
  documents: [{
    id: 'doc-1',
    name: 'Pay Stub - January 2023',
    type: 'Pay Stub',
    status: 'Verified',
    uploadDate: '2023-05-10'
  }, {
    id: 'doc-2',
    name: 'Bank Statement - Last 3 months',
    type: 'Bank Statement',
    status: 'Verified',
    uploadDate: '2023-05-12'
  }, {
    id: 'doc-3',
    name: 'Government ID',
    type: 'Identification',
    status: 'Verified',
    uploadDate: '2023-05-08'
  }, {
    id: 'doc-4',
    name: 'T4 - 2022',
    type: 'Tax Document',
    status: 'Pending Review',
    uploadDate: '2023-05-14'
  }],
  consents: [{
    id: 'consent-1',
    name: 'Credit Pull Authorization',
    status: 'Signed',
    date: '2023-05-05'
  }, {
    id: 'consent-2',
    name: 'Privacy Disclosure',
    status: 'Signed',
    date: '2023-05-05'
  }, {
    id: 'consent-3',
    name: 'Fee Agreement',
    status: 'Pending',
    date: null
  }],
  validationIssues: [{
    id: 'issue-1',
    type: 'Missing Information',
    description: 'Employment history incomplete',
    severity: 'Medium'
  }, {
    id: 'issue-2',
    type: 'Document Needed',
    description: 'Proof of down payment required',
    severity: 'High'
  }],
  creditReport: {
    bureau: 'Equifax',
    score: 720,
    reportDate: '2023-05-07',
    summary: 'Good credit history with no major issues. One late payment on credit card from 2021.',
    flags: [{
      type: 'Late Payment',
      description: 'Credit card payment 30 days late in March 2021',
      severity: 'Low'
    }],
    accounts: [{
      type: 'Mortgage',
      status: 'Current',
      balance: 0,
      paymentHistory: 'Excellent'
    }, {
      type: 'Credit Card',
      status: 'Current',
      balance: 2500,
      paymentHistory: 'Good with one late payment'
    }, {
      type: 'Auto Loan',
      status: 'Current',
      balance: 15000,
      paymentHistory: 'Excellent'
    }],
    inquiries: 2,
    utilization: 15
  },
  checklist: [{
    id: 'check-1',
    name: 'Government ID',
    status: 'Complete',
    required: true
  }, {
    id: 'check-2',
    name: 'Proof of Income',
    status: 'Complete',
    required: true
  }, {
    id: 'check-3',
    name: 'Bank Statements',
    status: 'Complete',
    required: true
  }, {
    id: 'check-4',
    name: 'Proof of Down Payment',
    status: 'Incomplete',
    required: true
  }, {
    id: 'check-5',
    name: 'Property Appraisal',
    status: 'Not Started',
    required: true
  }, {
    id: 'check-6',
    name: 'Gift Letter',
    status: 'Not Applicable',
    required: false
  }]
};
export function ApplicationDetail() {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const [activeTab, setActiveTab] = useState('details');
  const [application, setApplication] = useState(mockApplication);
  const [expandedSections, setExpandedSections] = useState({
    documents: true,
    consents: true,
    validation: true,
    creditReport: true,
    checklist: true
  });
  // Calculate application completion percentage
  const calculateCompletionPercentage = () => {
    // Count required items
    const requiredItems = application.checklist.filter(item => item.required);
    const completedItems = requiredItems.filter(item => item.status === 'Complete');
    // Calculate percentage
    const percentage = Math.round(completedItems.length / requiredItems.length * 100);
    return percentage;
  };
  // Get completion status text and color
  const getCompletionStatus = (percentage: number) => {
    if (percentage < 25) {
      return {
        text: 'Just Started',
        color: 'text-red-600',
        bgColor: 'bg-red-600'
      };
    } else if (percentage < 50) {
      return {
        text: 'In Progress',
        color: 'text-orange-600',
        bgColor: 'bg-orange-600'
      };
    } else if (percentage < 75) {
      return {
        text: 'Good Progress',
        color: 'text-yellow-600',
        bgColor: 'bg-yellow-600'
      };
    } else if (percentage < 100) {
      return {
        text: 'Almost Complete',
        color: 'text-blue-600',
        bgColor: 'bg-blue-600'
      };
    } else {
      return {
        text: 'Complete',
        color: 'text-green-600',
        bgColor: 'bg-green-600'
      };
    }
  };
  const completionPercentage = calculateCompletionPercentage();
  const completionStatus = getCompletionStatus(completionPercentage);
  // Fetch application data
  useEffect(() => {
    // In a real app, you would fetch the application data from an API
    console.log(`Fetching application with id: ${id}`);
    // For now, we'll just use the mock data
  }, [id]);
  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
  };
  const handleDeleteApplication = () => {
    if (window.confirm('Are you sure you want to delete this application?')) {
      console.log('Deleting application:', id);
      // In a real app, you would make an API call to delete the application
      // Then redirect to the applications list
      window.location.href = '/applications';
    }
  };
  const toggleSection = (section: string) => {
    setExpandedSections({
      ...expandedSections,
      [section]: !expandedSections[section]
    });
  };
  const getSeverityColor = (severity: string) => {
    switch (severity.toLowerCase()) {
      case 'high':
        return 'text-red-600 bg-red-50';
      case 'medium':
        return 'text-amber-600 bg-amber-50';
      case 'low':
        return 'text-blue-600 bg-blue-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };
  const renderDocumentStatus = (status: string) => {
    if (status === 'Verified') {
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-719">
          <CheckCircleIcon className="mr-1 h-3 w-3" data-id="element-720" />
          Verified
        </span>;
    } else {
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800" data-id="element-721">
          <AlertTriangleIcon className="mr-1 h-3 w-3" data-id="element-722" />
          Pending
        </span>;
    }
  };
  const renderConsentStatus = (status: string) => {
    if (status === 'Signed') {
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-723">
          <CheckIcon className="mr-1 h-3 w-3" data-id="element-724" />
          Signed
        </span>;
    } else {
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800" data-id="element-725">
          <AlertTriangleIcon className="mr-1 h-3 w-3" data-id="element-726" />
          Pending
        </span>;
    }
  };
  const renderChecklistStatus = (status: string) => {
    switch (status) {
      case 'Complete':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-727">
            <CheckCircleIcon className="mr-1 h-3 w-3" data-id="element-728" />
            Complete
          </span>;
      case 'Incomplete':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800" data-id="element-729">
            <AlertTriangleIcon className="mr-1 h-3 w-3" data-id="element-730" />
            Incomplete
          </span>;
      case 'Not Started':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-731">
            <FileIcon className="mr-1 h-3 w-3" data-id="element-732" />
            Not Started
          </span>;
      case 'Not Applicable':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-733">
            <XCircleIcon className="mr-1 h-3 w-3" data-id="element-734" />
            N/A
          </span>;
      default:
        return null;
    }
  };
  // Render the active tab content
  const renderTabContent = () => {
    switch (activeTab) {
      case 'details':
        return <div className="space-y-6" data-id="element-735">
            {/* Application Progress Tracker */}
            <div className="bg-white rounded-lg shadow-sm p-6" data-id="element-736">
              <div className="flex flex-col md:flex-row md:items-center justify-between mb-4" data-id="element-737">
                <div data-id="element-738">
                  <h2 className="text-xl font-semibold text-neutral-900 mb-1" data-id="element-739">
                    Application Progress
                  </h2>
                  <p className="text-sm text-neutral-500" data-id="element-740">
                    {completionStatus.text} - {completionPercentage}% of
                    required items completed
                  </p>
                </div>
                <div className={`text-lg font-bold ${completionStatus.color} mt-2 md:mt-0`} data-id="element-741">
                  {completionPercentage}%
                </div>
              </div>
              <div className="w-full bg-neutral-200 rounded-full h-4 mb-4" data-id="element-742">
                <div className={`${completionStatus.bgColor} h-4 rounded-full transition-all duration-500 ease-in-out`} style={{
                width: `${completionPercentage}%`
              }} data-id="element-743"></div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center" data-id="element-744">
                <div className="bg-neutral-50 rounded-lg p-3" data-id="element-745">
                  <p className="text-sm text-neutral-500 mb-1" data-id="element-746">Documents</p>
                  <p className="font-medium" data-id="element-747">
                    {application.documents.filter(doc => doc.status === 'Verified').length}
                    /{application.documents.length}
                  </p>
                </div>
                <div className="bg-neutral-50 rounded-lg p-3" data-id="element-748">
                  <p className="text-sm text-neutral-500 mb-1" data-id="element-749">Consents</p>
                  <p className="font-medium" data-id="element-750">
                    {application.consents.filter(consent => consent.status === 'Signed').length}
                    /{application.consents.length}
                  </p>
                </div>
                <div className="bg-neutral-50 rounded-lg p-3" data-id="element-751">
                  <p className="text-sm text-neutral-500 mb-1" data-id="element-752">Issues</p>
                  <p className="font-medium text-red-600" data-id="element-753">
                    {application.validationIssues.length}
                  </p>
                </div>
                <div className="bg-neutral-50 rounded-lg p-3" data-id="element-754">
                  <p className="text-sm text-neutral-500 mb-1" data-id="element-755">Checklist</p>
                  <p className="font-medium" data-id="element-756">
                    {application.checklist.filter(item => item.status === 'Complete' && item.required).length}
                    /
                    {application.checklist.filter(item => item.required).length}
                  </p>
                </div>
              </div>
            </div>
            {/* Application Overview */}
            <div className="bg-white rounded-lg shadow-sm p-6" data-id="element-757">
              <h2 className="text-2xl font-semibold text-neutral-900 mb-6" data-id="element-758">
                Application Details
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-759">
                <div data-id="element-760">
                  <p className="text-sm text-neutral-500" data-id="element-761">Loan Type</p>
                  <p className="font-medium" data-id="element-762">{application.loanType}</p>
                </div>
                <div data-id="element-763">
                  <p className="text-sm text-neutral-500" data-id="element-764">Loan Amount</p>
                  <p className="font-medium" data-id="element-765">
                    ${application.loanAmount.toLocaleString()}
                  </p>
                </div>
                <div data-id="element-766">
                  <p className="text-sm text-neutral-500" data-id="element-767">Property Address</p>
                  <p className="font-medium" data-id="element-768">{application.propertyAddress}</p>
                </div>
                <div data-id="element-769">
                  <p className="text-sm text-neutral-500" data-id="element-770">Status</p>
                  <p className="font-medium" data-id="element-771">{application.status}</p>
                </div>
                <div data-id="element-772">
                  <p className="text-sm text-neutral-500" data-id="element-773">Created</p>
                  <p className="font-medium" data-id="element-774">{application.createdAt}</p>
                </div>
                <div data-id="element-775">
                  <p className="text-sm text-neutral-500" data-id="element-776">Last Updated</p>
                  <p className="font-medium" data-id="element-777">{application.updatedAt}</p>
                </div>
              </div>
            </div>
            {/* KYC Status */}
            <div className="bg-white rounded-lg shadow-sm p-6" data-id="element-778">
              <div className="flex justify-between items-center mb-4" data-id="element-779">
                <div className="flex items-center" data-id="element-780">
                  <UserCheckIcon className="h-5 w-5 text-neutral-500 mr-2" data-id="element-781" />
                  <h2 className="text-lg font-semibold text-neutral-900" data-id="element-782">
                    KYC Status
                  </h2>
                </div>
                {application.kycStatus === 'Verified' ? <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800" data-id="element-783">
                    <CheckCircleIcon className="mr-1.5 h-4 w-4" data-id="element-784" />
                    Verified
                  </span> : <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800" data-id="element-785">
                    <AlertTriangleIcon className="mr-1.5 h-4 w-4" data-id="element-786" />
                    Pending
                  </span>}
              </div>
              <div className="flex flex-col md:flex-row md:items-center justify-between bg-blue-50 p-4 rounded-md" data-id="element-787">
                <div className="flex items-center mb-3 md:mb-0" data-id="element-788">
                  <CheckCircleIcon className="h-5 w-5 text-blue-600 mr-2" data-id="element-789" />
                  <span className="text-blue-800 font-medium" data-id="element-790">
                    Identity verification complete
                  </span>
                </div>
                <button className="inline-flex items-center px-3 py-1.5 border border-blue-600 text-sm font-medium rounded text-blue-600 hover:bg-blue-100 transition-colors" data-id="element-791">
                  <div className="mr-1.5 h-4 w-4" data-id="element-792" />
                  Re-verify
                </button>
              </div>
            </div>
            {/* Documents Section */}
            <div className="bg-white rounded-lg shadow-sm overflow-hidden" data-id="element-793">
              <div className="flex justify-between items-center p-6 cursor-pointer" onClick={() => toggleSection('documents')} data-id="element-794">
                <div className="flex items-center" data-id="element-795">
                  <FileTextIcon className="h-5 w-5 text-neutral-500 mr-2" data-id="element-796" />
                  <h2 className="text-lg font-semibold text-neutral-900" data-id="element-797">
                    Uploaded Documents
                  </h2>
                </div>
                <button data-id="element-798">
                  {expandedSections.documents ? <ChevronDownIcon className="h-5 w-5 text-neutral-500" data-id="element-799" /> : <ChevronRightIcon className="h-5 w-5 text-neutral-500" data-id="element-800" />}
                </button>
              </div>
              {expandedSections.documents && <div className="px-6 pb-6" data-id="element-801">
                  <div className="flex justify-between items-center mb-4" data-id="element-802">
                    <p className="text-sm text-neutral-500" data-id="element-803">
                      {application.documents.length} documents uploaded
                    </p>
                    <button className="inline-flex items-center px-3 py-1.5 bg-blue-600 text-sm font-medium rounded text-white hover:bg-blue-700 transition-colors" data-id="element-804">
                      <PlusIcon className="mr-1.5 h-4 w-4" data-id="element-805" />
                      Upload Document
                    </button>
                  </div>
                  <div className="divide-y divide-neutral-200" data-id="element-806">
                    {application.documents.map(doc => <div key={doc.id} className="py-3 flex flex-col md:flex-row md:items-center justify-between" data-id="element-807">
                        <div className="mb-2 md:mb-0" data-id="element-808">
                          <h3 className="text-sm font-medium text-neutral-900" data-id="element-809">
                            {doc.name}
                          </h3>
                          <p className="text-xs text-neutral-500" data-id="element-810">
                            {doc.type} • Uploaded on {doc.uploadDate}
                          </p>
                        </div>
                        <div className="flex items-center space-x-3" data-id="element-811">
                          {renderDocumentStatus(doc.status)}
                          <button className="text-neutral-400 hover:text-neutral-600" data-id="element-812">
                            <DownloadIcon className="h-4 w-4" data-id="element-813" />
                          </button>
                        </div>
                      </div>)}
                  </div>
                </div>}
            </div>
            {/* Signed Consents */}
            <div className="bg-white rounded-lg shadow-sm overflow-hidden" data-id="element-814">
              <div className="flex justify-between items-center p-6 cursor-pointer" onClick={() => toggleSection('consents')} data-id="element-815">
                <div className="flex items-center" data-id="element-816">
                  <ClipboardCheckIcon className="h-5 w-5 text-neutral-500 mr-2" data-id="element-817" />
                  <h2 className="text-lg font-semibold text-neutral-900" data-id="element-818">
                    Signed Consents
                  </h2>
                </div>
                <button data-id="element-819">
                  {expandedSections.consents ? <ChevronDownIcon className="h-5 w-5 text-neutral-500" data-id="element-820" /> : <ChevronRightIcon className="h-5 w-5 text-neutral-500" data-id="element-821" />}
                </button>
              </div>
              {expandedSections.consents && <div className="px-6 pb-6" data-id="element-822">
                  <div className="flex justify-between items-center mb-4" data-id="element-823">
                    <p className="text-sm text-neutral-500" data-id="element-824">
                      {application.consents.filter(c => c.status === 'Signed').length}{' '}
                      of {application.consents.length} consents signed
                    </p>
                    <div className="flex space-x-2" data-id="element-825">
                      <button className="inline-flex items-center px-3 py-1.5 border border-neutral-300 text-sm font-medium rounded text-neutral-700 hover:bg-neutral-50 transition-colors" data-id="element-826">
                        <ExternalLinkIcon className="mr-1.5 h-4 w-4" data-id="element-827" />
                        Send via DocuSign
                      </button>
                    </div>
                  </div>
                  <div className="divide-y divide-neutral-200" data-id="element-828">
                    {application.consents.map(consent => <div key={consent.id} className="py-3 flex flex-col md:flex-row md:items-center justify-between" data-id="element-829">
                        <div className="mb-2 md:mb-0" data-id="element-830">
                          <h3 className="text-sm font-medium text-neutral-900" data-id="element-831">
                            {consent.name}
                          </h3>
                          <p className="text-xs text-neutral-500" data-id="element-832">
                            {consent.status === 'Signed' ? `Signed on ${consent.date}` : 'Not signed yet'}
                          </p>
                        </div>
                        <div className="flex items-center space-x-3" data-id="element-833">
                          {renderConsentStatus(consent.status)}
                          {consent.status === 'Signed' && <button className="text-neutral-400 hover:text-neutral-600" data-id="element-834">
                              <DownloadIcon className="h-4 w-4" data-id="element-835" />
                            </button>}
                        </div>
                      </div>)}
                  </div>
                </div>}
            </div>
            {/* Validation Issues */}
            <div className="bg-white rounded-lg shadow-sm overflow-hidden" data-id="element-836">
              <div className="flex justify-between items-center p-6 cursor-pointer" onClick={() => toggleSection('validation')} data-id="element-837">
                <div className="flex items-center" data-id="element-838">
                  <AlertTriangleIcon className="h-5 w-5 text-neutral-500 mr-2" data-id="element-839" />
                  <h2 className="text-lg font-semibold text-neutral-900" data-id="element-840">
                    Validation Issues
                  </h2>
                  {application.validationIssues.length > 0 && <span className="ml-2 inline-flex items-center justify-center w-5 h-5 text-xs font-medium rounded-full bg-red-100 text-red-800" data-id="element-841">
                      {application.validationIssues.length}
                    </span>}
                </div>
                <button data-id="element-842">
                  {expandedSections.validation ? <ChevronDownIcon className="h-5 w-5 text-neutral-500" data-id="element-843" /> : <ChevronRightIcon className="h-5 w-5 text-neutral-500" data-id="element-844" />}
                </button>
              </div>
              {expandedSections.validation && <div className="px-6 pb-6" data-id="element-845">
                  {application.validationIssues.length === 0 ? <div className="flex items-center justify-center p-4 bg-green-50 rounded-md" data-id="element-846">
                      <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" data-id="element-847" />
                      <p className="text-green-700 font-medium" data-id="element-848">
                        No validation issues found
                      </p>
                    </div> : <div className="space-y-3" data-id="element-849">
                      {application.validationIssues.map(issue => <div key={issue.id} className="flex items-start p-3 rounded-md bg-neutral-50" data-id="element-850">
                          <div className={`flex-shrink-0 rounded-full p-1 ${getSeverityColor(issue.severity)}`} data-id="element-851">
                            <AlertTriangleIcon className="h-4 w-4" data-id="element-852" />
                          </div>
                          <div className="ml-3" data-id="element-853">
                            <h3 className="text-sm font-medium text-neutral-900" data-id="element-854">
                              {issue.type}
                            </h3>
                            <p className="text-xs text-neutral-600" data-id="element-855">
                              {issue.description}
                            </p>
                          </div>
                          <div className="ml-auto pl-3" data-id="element-856">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getSeverityColor(issue.severity)}`} data-id="element-857">
                              {issue.severity}
                            </span>
                          </div>
                        </div>)}
                    </div>}
                </div>}
            </div>
            {/* Credit Report */}
            <div className="bg-white rounded-lg shadow-sm overflow-hidden" data-id="element-858">
              <div className="flex justify-between items-center p-6 cursor-pointer" onClick={() => toggleSection('creditReport')} data-id="element-859">
                <div className="flex items-center" data-id="element-860">
                  <CreditCardIcon className="h-5 w-5 text-neutral-500 mr-2" data-id="element-861" />
                  <h2 className="text-lg font-semibold text-neutral-900" data-id="element-862">
                    Credit Report
                  </h2>
                </div>
                <button data-id="element-863">
                  {expandedSections.creditReport ? <ChevronDownIcon className="h-5 w-5 text-neutral-500" data-id="element-864" /> : <ChevronRightIcon className="h-5 w-5 text-neutral-500" data-id="element-865" />}
                </button>
              </div>
              {expandedSections.creditReport && <div className="px-6 pb-6" data-id="element-866">
                  <div className="flex flex-col md:flex-row md:items-center justify-between mb-6" data-id="element-867">
                    <div className="mb-3 md:mb-0" data-id="element-868">
                      <div className="flex items-center" data-id="element-869">
                        <h3 className="text-base font-medium text-neutral-900" data-id="element-870">
                          {application.creditReport.bureau} Credit Report
                        </h3>
                        <span className="ml-2 text-xs text-neutral-500" data-id="element-871">
                          Pulled on {application.creditReport.reportDate}
                        </span>
                      </div>
                    </div>
                    <div className="flex space-x-2" data-id="element-872">
                      <button className="inline-flex items-center px-3 py-1.5 border border-neutral-300 text-sm font-medium rounded text-neutral-700 hover:bg-neutral-50 transition-colors" data-id="element-873">
                        <DownloadIcon className="mr-1.5 h-4 w-4" data-id="element-874" />
                        Download Report
                      </button>
                      <button className="inline-flex items-center px-3 py-1.5 bg-blue-600 text-sm font-medium rounded text-white hover:bg-blue-700 transition-colors" data-id="element-875">
                        <div className="mr-1.5 h-4 w-4" data-id="element-876" />
                        Refresh
                      </button>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6" data-id="element-877">
                    <div className="bg-neutral-50 rounded-lg p-4 flex flex-col items-center justify-center" data-id="element-878">
                      <p className="text-sm text-neutral-500 mb-1" data-id="element-879">
                        Credit Score
                      </p>
                      <div className="flex items-center" data-id="element-880">
                        <div className="text-3xl font-bold text-neutral-900" data-id="element-881">
                          {application.creditReport.score}
                        </div>
                        <div className="ml-2 text-sm" data-id="element-882">
                          {application.creditReport.score >= 740 ? <span className="text-green-600" data-id="element-883">Excellent</span> : application.creditReport.score >= 670 ? <span className="text-blue-600" data-id="element-884">Good</span> : application.creditReport.score >= 580 ? <span className="text-yellow-600" data-id="element-885">Fair</span> : <span className="text-red-600" data-id="element-886">Poor</span>}
                        </div>
                      </div>
                    </div>
                    <div className="bg-neutral-50 rounded-lg p-4" data-id="element-887">
                      <p className="text-sm text-neutral-500 mb-1" data-id="element-888">
                        Inquiries (Last 2 Years)
                      </p>
                      <p className="text-xl font-bold text-neutral-900" data-id="element-889">
                        {application.creditReport.inquiries}
                      </p>
                    </div>
                    <div className="bg-neutral-50 rounded-lg p-4" data-id="element-890">
                      <p className="text-sm text-neutral-500 mb-1" data-id="element-891">
                        Credit Utilization
                      </p>
                      <p className="text-xl font-bold text-neutral-900" data-id="element-892">
                        {application.creditReport.utilization}%
                      </p>
                    </div>
                  </div>
                  <div className="mb-6" data-id="element-893">
                    <h3 className="text-base font-medium text-neutral-900 mb-3 flex items-center" data-id="element-894">
                      <BrainIcon className="h-4 w-4 mr-2 text-neutral-500" data-id="element-895" />
                      AI Summary
                    </h3>
                    <div className="bg-blue-50 rounded-lg p-4 text-sm text-blue-800" data-id="element-896">
                      {application.creditReport.summary}
                    </div>
                  </div>
                  {application.creditReport.flags.length > 0 && <div className="mb-6" data-id="element-897">
                      <h3 className="text-base font-medium text-neutral-900 mb-3" data-id="element-898">
                        Flags
                      </h3>
                      <div className="space-y-2" data-id="element-899">
                        {application.creditReport.flags.map((flag, index) => <div key={index} className="flex items-start p-3 rounded-md bg-neutral-50" data-id="element-900">
                            <div className={`flex-shrink-0 rounded-full p-1 ${getSeverityColor(flag.severity)}`} data-id="element-901">
                              <AlertTriangleIcon className="h-4 w-4" data-id="element-902" />
                            </div>
                            <div className="ml-3" data-id="element-903">
                              <h4 className="text-sm font-medium text-neutral-900" data-id="element-904">
                                {flag.type}
                              </h4>
                              <p className="text-xs text-neutral-600" data-id="element-905">
                                {flag.description}
                              </p>
                            </div>
                          </div>)}
                      </div>
                    </div>}
                  <div data-id="element-906">
                    <h3 className="text-base font-medium text-neutral-900 mb-3" data-id="element-907">
                      Accounts
                    </h3>
                    <div className="overflow-x-auto" data-id="element-908">
                      <table className="min-w-full divide-y divide-neutral-200" data-id="element-909">
                        <thead data-id="element-910">
                          <tr data-id="element-911">
                            <th className="px-3 py-2 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider" data-id="element-912">
                              Type
                            </th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider" data-id="element-913">
                              Status
                            </th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider" data-id="element-914">
                              Balance
                            </th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider" data-id="element-915">
                              Payment History
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-neutral-200" data-id="element-916">
                          {application.creditReport.accounts.map((account, index) => <tr key={index} data-id="element-917">
                                <td className="px-3 py-2 whitespace-nowrap text-sm text-neutral-900" data-id="element-918">
                                  {account.type}
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap text-sm" data-id="element-919">
                                  {account.status === 'Current' ? <span className="text-green-600" data-id="element-920">
                                      Current
                                    </span> : <span className="text-red-600" data-id="element-921">
                                      {account.status}
                                    </span>}
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap text-sm text-neutral-900" data-id="element-922">
                                  ${account.balance.toLocaleString()}
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap text-sm text-neutral-900" data-id="element-923">
                                  {account.paymentHistory}
                                </td>
                              </tr>)}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>}
            </div>
            {/* Document Checklist */}
            <div className="bg-white rounded-lg shadow-sm overflow-hidden" data-id="element-924">
              <div className="flex justify-between items-center p-6 cursor-pointer" onClick={() => toggleSection('checklist')} data-id="element-925">
                <div className="flex items-center" data-id="element-926">
                  <ClipboardCheckIcon className="h-5 w-5 text-neutral-500 mr-2" data-id="element-927" />
                  <h2 className="text-lg font-semibold text-neutral-900" data-id="element-928">
                    Document Checklist
                  </h2>
                </div>
                <button data-id="element-929">
                  {expandedSections.checklist ? <ChevronDownIcon className="h-5 w-5 text-neutral-500" data-id="element-930" /> : <ChevronRightIcon className="h-5 w-5 text-neutral-500" data-id="element-931" />}
                </button>
              </div>
              {expandedSections.checklist && <div className="px-6 pb-6" data-id="element-932">
                  <div className="flex justify-between items-center mb-4" data-id="element-933">
                    <p className="text-sm text-neutral-500" data-id="element-934">
                      {application.checklist.filter(item => item.status === 'Complete').length}{' '}
                      of{' '}
                      {application.checklist.filter(item => item.required).length}{' '}
                      required items complete
                    </p>
                    <button className="text-sm text-blue-600 hover:text-blue-800 font-medium" data-id="element-935">
                      Generate Consultation Template
                    </button>
                  </div>
                  <div className="divide-y divide-neutral-200" data-id="element-936">
                    {application.checklist.map(item => <div key={item.id} className="py-3 flex flex-col md:flex-row md:items-center justify-between" data-id="element-937">
                        <div className="mb-2 md:mb-0 flex items-center" data-id="element-938">
                          <div className="mr-3" data-id="element-939">
                            {item.status === 'Complete' ? <CheckCircleIcon className="h-5 w-5 text-green-500" data-id="element-940" /> : item.status === 'Incomplete' ? <AlertTriangleIcon className="h-5 w-5 text-yellow-500" data-id="element-941" /> : item.status === 'Not Started' ? <FileIcon className="h-5 w-5 text-neutral-400" data-id="element-942" /> : <XCircleIcon className="h-5 w-5 text-blue-500" data-id="element-943" />}
                          </div>
                          <div data-id="element-944">
                            <h3 className="text-sm font-medium text-neutral-900" data-id="element-945">
                              {item.name}
                            </h3>
                            <p className="text-xs text-neutral-500" data-id="element-946">
                              {item.required ? 'Required' : 'Optional'}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center" data-id="element-947">
                          {renderChecklistStatus(item.status)}
                        </div>
                      </div>)}
                  </div>
                </div>}
            </div>
          </div>;
      case 'documents':
        return <DocumentsSection applicationId={id || ''} data-id="element-948" />;
      case 'downPayment':
        return <DownPaymentSection application={application} data-id="element-949" />;
      case 'income':
        return <IncomeSection applicationId={id || ''} data-id="element-950" />;
      case 'rateAi':
        return <RateAITab data-id="element-951" />;
      default:
        return <div data-id="element-952">Tab content not found</div>;
    }
  };
  return <div className="grid grid-cols-1 md:grid-cols-4 gap-6" data-id="element-953">
      <div className="md:col-span-1" data-id="element-954">
        <ApplicationSidebar application={application} activeTab={activeTab} onTabChange={handleTabChange} onDeleteApplication={handleDeleteApplication} data-id="element-955" />
      </div>
      <div className="md:col-span-3" data-id="element-956">{renderTabContent()}</div>
    </div>;
}